import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AccessBanner } from "@/components/AccessBanner";
import { DemoBanner } from "@/components/DemoBanner";
import { StuckAlert } from "@/components/StuckAlert";
import { GuidedTour } from "@/components/GuidedTour";
import { MessageAckPopup } from "@/components/MessageAckPopup";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LaunchOfferStrip } from "@/components/LaunchOffer";
import { InstallAppButton } from "@/components/InstallAppButton";
import {
  Lightbulb,
  LayoutDashboard,
  DoorClosed,
  Users,
  UserCog,
  ClipboardCheck,
  BarChart3,
  CreditCard,
  Wrench,
  LogOut,
  Sparkles,
  MapPin,
  Repeat,
  ClipboardList,
  CalendarClock,
  GanttChartSquare,
  MessageSquare,
  ShieldAlert,
  ArrowLeft,
  Eye,
  Menu,
} from "lucide-react";

const NAV = [
  { to: "/manager", end: true, label: "Daily List", icon: LayoutDashboard, testid: "nav-dashboard", mobile: true },
  { to: "/manager/timeline", label: "Timeline", icon: GanttChartSquare, testid: "nav-timeline", mobile: true },
  { to: "/manager/rooms", label: "Rooms & Tasks", icon: DoorClosed, testid: "nav-rooms" },
  { to: "/manager/tasks", label: "Scheduled Tasks", icon: Repeat, testid: "nav-tasks", mobile: true },
  { to: "/manager/todo", label: "My To-Do", icon: ClipboardList, testid: "nav-todo", mobile: true },
  { to: "/manager/cleaners", label: "Cleaners", icon: Users, testid: "nav-cleaners", mobile: true },
  { to: "/manager/managers", label: "Owners / Managers", icon: UserCog, testid: "nav-managers" },
  { to: "/manager/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages", mobile: true },
  { to: "/clean", label: "Clean Rooms", icon: Sparkles, testid: "nav-clean-rooms" },
  { to: "/manager/view-as", label: "View as cleaner", icon: Eye, testid: "nav-view-as" },
  { to: "/manager/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance" },
  { to: "/manager/timesheets", label: "Timesheets", icon: ClipboardCheck, testid: "nav-timesheets" },
  { to: "/manager/leave", label: "Leave Requests", icon: CalendarClock, testid: "nav-leave" },
  { to: "/manager/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", mobile: true },
  { to: "/manager/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/manager/requests", label: "Requests & Feedback", icon: Lightbulb, testid: "nav-requests", mobile: true },
];

const DEMO_TOUR_STEPS = [
  { route: "/manager", testid: "open-upload-btn", title: "Import your day",
    body: "Upload your daily room-status CSV here — KlenaPro turns it into assigned cleaning jobs, deciding which rooms need a full clean or a quick service." },
  { route: "/manager", testid: "demo-role-select", title: "See the cleaner's view",
    body: "Switch to any cleaner from the demo bar to open the phone app your staff use on the floor — strip, start and tick off rooms." },
  { route: "/manager/timesheets", testid: "timesheets-tabs", title: "Payroll-ready timesheets",
    body: "Review, correct and sign off weekly timesheets. Leave days show a label such as \"Rostered day off\" or \"Sick leave\"." },
  { route: "/manager/reports", testid: "report-download-panel", title: "Download PDF reports",
    body: "Generate by-room, by-cleaner or summary reports for any date range — preview them in-app or download a PDF." },
];

export default function ManagerLayout() {
  const { user, motel, logout, returnToAdmin, impersonate } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isBilling = useLocation().pathname.endsWith("/billing");
  const [menuOpen, setMenuOpen] = useState(false);
  const impersonating = user?.impersonating;
  const ghost = user?.ghost;
  const switchToFull = async () => {
    try {
      await impersonate(user.motel_id, false);
      navigate("/manager");
    } catch { /* ignore */ }
  };
  const { data: leavePending } = useQuery({
    queryKey: ["leave-pending-count"],
    queryFn: async () => (await api.get("/leave-requests/pending-count")).data,
    refetchInterval: 60000,
  });
  const pendingLeave = leavePending?.count || 0;

  const { data: unreadMsgs } = useQuery({
    queryKey: ["messages-unread-count"],
    queryFn: async () => (await api.get("/messages/unread-count")).data,
    refetchInterval: 45000,
  });
  const unreadCount = unreadMsgs?.count || 0;

  const goBackToAdmin = async () => {
    await returnToAdmin();
    navigate("/admin");
  };

  return (
    <div className="flex min-h-screen bg-background">
      {impersonating && (
        <div
          data-testid="impersonation-banner"
          className={`fixed inset-x-0 top-0 z-30 flex flex-wrap items-center justify-center gap-3 px-4 py-2 text-sm font-semibold ${
            ghost ? "bg-slate-800 text-slate-100" : "bg-amber-500 text-amber-950"
          }`}
        >
          {ghost ? <Eye className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
          <span data-testid="impersonation-label">
            {ghost
              ? `Super Admin — VIEW-ONLY (ghost) on ${motel?.name || "this motel"} · editing disabled`
              : `Super Admin — FULL ACCESS on ${motel?.name || "this motel"}`}
          </span>
          {ghost && (
            <Button
              size="sm"
              variant="outline"
              data-testid="switch-to-full"
              onClick={switchToFull}
              className="h-7 gap-1.5 border-slate-500 bg-slate-700 text-white hover:bg-slate-600"
            >
              <ShieldAlert className="h-3.5 w-3.5" /> Switch to full access
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            data-testid="return-to-admin"
            onClick={goBackToAdmin}
            className={`h-7 gap-1.5 ${ghost ? "border-slate-500 bg-slate-100 text-slate-900 hover:bg-white" : "border-amber-700 bg-amber-100 text-amber-950 hover:bg-amber-200"}`}
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Return to Super Admin
          </Button>
        </div>
      )}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-border bg-card md:flex ${impersonating ? "pt-10" : ""}`}>
        <div className="flex shrink-0 items-center gap-2.5 border-b border-border px-6 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary">
            <Sparkles className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <div className="font-display text-lg font-extrabold leading-none">KlenaPro</div>
            <div className="label-caps text-muted-foreground">{t("Manager")}</div>
          </div>
        </div>

        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `k-press flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {t(n.label)}
              {n.to === "/manager/leave" && pendingLeave > 0 && (
                <span
                  data-testid="nav-leave-badge"
                  className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[11px] font-bold text-white"
                >
                  {pendingLeave}
                </span>
              )}
              {n.to === "/manager/messages" && unreadCount > 0 && (
                <span
                  data-testid="nav-messages-badge"
                  className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-indigo-500 px-1.5 py-0.5 text-[11px] font-bold text-white"
                >
                  {unreadCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="shrink-0 border-t border-border p-4">
          <div className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
            {motel?.name || "—"}
          </div>
          <div className="mb-3 truncate text-sm font-medium">{user?.name}</div>
          <div className="mb-3">
            <LanguageSwitcher />
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid="logout-button"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="k-press w-full gap-2"
          >
            <LogOut className="h-4 w-4" />
            {t("Sign out")}
          </Button>
          <InstallAppButton className="k-press mt-2 w-full" />
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className={`fixed inset-x-0 z-20 flex items-center justify-between border-b border-border bg-card/90 px-4 py-3 backdrop-blur md:hidden ${impersonating ? "top-10" : "top-0"}`}>
        <div className="flex items-center gap-2">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="mobile-menu-btn" aria-label={t("Menu")}>
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0" data-testid="mobile-menu-drawer">
              <div className="flex h-full flex-col">
                <div className="flex shrink-0 items-center gap-2.5 border-b border-border px-5 py-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary">
                    <Sparkles className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <div className="font-display text-lg font-extrabold leading-none">KlenaPro</div>
                    <div className="label-caps text-muted-foreground">{t("Manager")}</div>
                  </div>
                </div>
                <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
                  {NAV.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      data-testid={`m-${n.testid}`}
                      onClick={() => setMenuOpen(false)}
                      className={({ isActive }) =>
                        `k-press flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium ${
                          isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                        }`
                      }
                    >
                      <n.icon className="h-4 w-4" />
                      {t(n.label)}
                      {n.to === "/manager/leave" && pendingLeave > 0 && (
                        <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[11px] font-bold text-white">{pendingLeave}</span>
                      )}
                      {n.to === "/manager/messages" && unreadCount > 0 && (
                        <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-indigo-500 px-1.5 py-0.5 text-[11px] font-bold text-white">{unreadCount}</span>
                      )}
                    </NavLink>
                  ))}
                </nav>
                <div className="shrink-0 border-t border-border p-4">
                  <div className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" />
                    {motel?.name || "—"}
                  </div>
                  <div className="mb-3 truncate text-sm font-medium">{user?.name}</div>
                  <div className="mb-3"><LanguageSwitcher /></div>
                  <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/login"); }} className="k-press w-full gap-2">
                    <LogOut className="h-4 w-4" /> {t("Sign out")}
                  </Button>
                  <InstallAppButton className="k-press mt-2 w-full" />
                </div>
              </div>
            </SheetContent>
          </Sheet>
          <div className="font-display text-lg font-extrabold">KlenaPro</div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/login"); }}>
          <LogOut className="h-4 w-4" />
        </Button>
      </div>

      <main className="flex-1 md:ml-64">
        <div className={`mx-auto max-w-7xl px-4 pb-16 sm:px-6 ${impersonating ? "pt-28 md:pt-16" : "pt-20 md:pt-8"}`}>
          <DemoBanner />
          <AccessBanner />
          {!isBilling && <div className="mb-4 overflow-hidden rounded-md"><LaunchOfferStrip /></div>}
          <StuckAlert />
          <Outlet />
        </div>
        {motel?.is_demo && (
          <GuidedTour steps={DEMO_TOUR_STEPS} storageKey={`klena_demo_tour_${motel?.id || "x"}`} run />
        )}
        {/* mobile bottom nav */}
        <div className="fixed inset-x-0 bottom-0 z-20 flex justify-around border-t border-border bg-card/95 py-1.5 backdrop-blur md:hidden">
          {NAV.filter((n) => n.mobile).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] font-medium ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <n.icon className="h-5 w-5" />
              {t(n.label).split(" ")[0]}
            </NavLink>
          ))}
        </div>
      </main>
      <MessageAckPopup />
    </div>
  );
}
